import {
  applyActionCode, confirmPasswordReset, createUserWithEmailAndPassword, getAdditionalUserInfo, GoogleAuthProvider, onAuthStateChanged, RecaptchaVerifier,
  sendEmailVerification, sendPasswordResetEmail, signInWithCredential, signInWithEmailAndPassword, signInWithPhoneNumber, signInWithPopup, signOut,
  updateProfile, verifyPasswordResetCode, type ConfirmationResult, type User,
} from 'firebase/auth';

import { phoneSignInEnabled } from '../../config/env.ts';
import type { AuthPort, SignedInUser, SignUpDetails } from '../ports.ts';
import type { FirebaseServices } from './app.ts';

function toUser(user: User | null): SignedInUser | null {
  return user ? { uid: user.uid, email: user.email, displayName: user.displayName, emailVerified: user.emailVerified, phone: user.phoneNumber } : null;
}

// In the Capacitor apps, Google sign-in runs natively (Credential Manager on
// Android, Google Sign-In on iOS) through @capacitor-firebase/authentication
// and hands back an ID token; on the web this returns null.
async function nativeGoogleIdToken(): Promise<string | null> {
  const { Capacitor } = await import('@capacitor/core');
  if (!Capacitor.isNativePlatform()) return null;
  const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
  const result = await FirebaseAuthentication.signInWithGoogle({ skipNativeAuth: true });
  const token = result.credential?.idToken;
  if (!token) throw Object.assign(new Error('Google sign-in was cancelled.'), { code: 'auth/popup-closed-by-user' });
  return token;
}

// Where links in Firebase's emails send people back to.
function continueUrl(): string | undefined {
  return typeof location === 'undefined' ? undefined : `${location.origin}/auth/sign-in`;
}

// Turns Firebase's error codes into what the person should do.
function authErrorMessage(error: unknown): string {
  const code = String((error as { code?: string })?.code || '');
  const messages: Record<string, string> = {
    'auth/email-already-in-use': 'An account with this email already exists. Sign in instead.',
    'auth/invalid-email': 'Enter a valid email address.',
    'auth/weak-password': 'Choose a stronger password.',
    'auth/invalid-credential': 'That email and password don’t match an account.',
    'auth/wrong-password': 'That email and password don’t match an account.',
    'auth/user-not-found': 'That email and password don’t match an account.',
    'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
    'auth/network-request-failed': 'Guildite could not be reached. Check your connection and try again.',
    'auth/popup-closed-by-user': 'The Google window was closed before you finished.',
    'auth/expired-action-code': 'This link has expired. Ask for a new one.',
    'auth/invalid-action-code': 'This link was already used or is not valid. Ask for a new one.',
    'auth/user-disabled': 'This account is suspended. Contact support.',
    'auth/invalid-phone-number': 'Enter a valid phone number.',
    'auth/missing-phone-number': 'Enter your phone number.',
    'auth/invalid-verification-code': 'That code is not right. Check the text and try again.',
    'auth/code-expired': 'That code has expired. Ask for a new one.',
    'auth/missing-verification-code': 'Enter the 6-digit code.',
    'auth/quota-exceeded': 'Too many codes were sent. Try again later.',
    'auth/captcha-check-failed': 'The security check failed. Try again.',
    'auth/operation-not-allowed': 'Signing in with a phone number is not available yet. Use your email.',
  };
  return messages[code] ?? 'Something went wrong. Please try again.';
}

// Every failure reaches screens as a plain message, never a Firebase code.
function friendly<A extends unknown[], R>(task: (...args: A) => Promise<R>): (...args: A) => Promise<R> {
  return async (...args) => {
    try {
      return await task(...args);
    } catch (error) {
      throw new Error(authErrorMessage(error), { cause: error });
    }
  };
}

export function createFirebaseAuth(services: FirebaseServices): AuthPort {
  const { auth } = services;

  // The private account record, in the shape the live app writes at sign-up,
  // so accounts made in v2 work in the old app too. Owner-only by the rules.
  async function writeAccountRecord(user: User, details: { name: string; country?: string; town?: string }) {
    // The login phone stays in this private record only (never on a profile).
    const [{ get, ref, set }, database] = await Promise.all([import('firebase/database'), services.database()]);
    const record = ref(database, `users/${user.uid}`);
    if ((await get(record)).exists()) return;
    await set(record, {
      name: details.name, phone: user.phoneNumber || '', email: user.email || '', profilePictureUrl: user.photoURL || '',
      role: 'user', approved: true, banned: false, createdAt: new Date().toISOString(), registrationDate: Date.now(),
      ...(details.country ? { country: details.country } : {}), ...(details.town ? { town: details.town } : {}),
    });
  }

  // The Auth emulator skips the reCAPTCHA and sends no texts.
  if (services.emulated) auth.settings.appVerificationDisabledForTesting = true;
  let confirmation: ConfirmationResult | null = null;
  let verifier: RecaptchaVerifier | null = null;
  // Not in the Android and iOS apps yet: they need the native phone sign-in.
  const nativeApp = typeof window !== 'undefined' && Boolean((window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.());

  return {
    current: () => toUser(auth.currentUser),
    onChange: listener => onAuthStateChanged(auth, user => listener(toUser(user))),
    signInWithEmail: friendly(async (email: string, password: string) => {
      await signInWithEmailAndPassword(auth, email, password);
    }),
    signUp: friendly(async ({ name, email, password, country, town }: SignUpDetails) => {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      await updateProfile(user, { displayName: name });
      await writeAccountRecord(user, { name, country, town });
      // Not blocking: people can use Guildite while the email waits.
      await sendEmailVerification(user, { url: continueUrl() ?? '' }).catch(() => undefined);
    }),
    signInWithGoogle: friendly(async () => {
      // Popups don't work inside the Android and iOS apps: there the native
      // Google sign-in returns an ID token that signs in to Firebase here.
      const native = await nativeGoogleIdToken();
      const { user } = native
        ? await signInWithCredential(auth, GoogleAuthProvider.credential(native))
        : await signInWithPopup(auth, new GoogleAuthProvider());
      await writeAccountRecord(user, { name: user.displayName || 'Guildite member' });
    }),
    phone: {
      available: phoneSignInEnabled && !nativeApp,
      sendCode: friendly(async (phone: string, anchorId: string) => {
        verifier?.clear();
        verifier = new RecaptchaVerifier(auth, anchorId, { size: 'invisible' });
        confirmation = await signInWithPhoneNumber(auth, phone, verifier);
      }),
      confirm: friendly(async (code: string, details?: { name: string; country: string; town: string }) => {
        if (!confirmation) throw Object.assign(new Error('No code was sent.'), { code: 'auth/code-expired' });
        const result = await confirmation.confirm(code.replace(/\D/g, ''));
        confirmation = null;
        const isNew = getAdditionalUserInfo(result)?.isNewUser === true;
        if (isNew && details?.name) await updateProfile(result.user, { displayName: details.name });
        await writeAccountRecord(result.user, details ?? { name: result.user.displayName || 'Guildite member' });
        return { isNew };
      }),
    },
    sendPasswordReset: friendly(async (email: string) => {
      await sendPasswordResetEmail(auth, email, { url: continueUrl() ?? '' });
    }),
    sendVerificationEmail: friendly(async () => {
      if (auth.currentUser) await sendEmailVerification(auth.currentUser, { url: continueUrl() ?? '' });
    }),
    async refresh() {
      await auth.currentUser?.reload();
      // The Worker reads email_verified from the token, so fetch a fresh one.
      await auth.currentUser?.getIdToken(true).catch(() => undefined);
      return toUser(auth.currentUser);
    },
    applyEmailAction: friendly((code: string) => applyActionCode(auth, code)),
    checkResetCode: friendly((code: string) => verifyPasswordResetCode(auth, code)),
    resetPassword: friendly((code: string, password: string) => confirmPasswordReset(auth, code, password)),
    signOut: () => signOut(auth),
    idToken: async () => (auth.currentUser ? auth.currentUser.getIdToken() : null),
  };
}
